import React, { useState } from "react";
import axiosInstance from "../../../services/axiosInstance";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  MenuItem,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Close,
  Person,
  AdminPanelSettings,
  VisibilityOutlined,
} from "@mui/icons-material";
import apiEndPointsConfig from "../../../services/apiEndPoints";

/* -------------------- Validation Schema -------------------- */
const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Minimum 2 characters")
    .max(50, "Maximum 50 characters")
    .trim(),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required")
    .lowercase()
    .trim(),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Minimum 8 characters"),
  role: Yup.string()
    .required("Role is required")
    .oneOf(["member", "admin", "viewer"]),
});

/* -------------------- Component -------------------- */
const AddTeamMemberModal = ({ open, onClose, clientId, onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const initialValues = {
    name: "",
    email: "",
    password: "",
    role: "member",
  };

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setFieldError }
  ) => {
    setMessage({ type: "", text: "" });

    try {
      const response = await axiosInstance.post(
        apiEndPointsConfig.addTeamMember,
        { ...values, clientId }
      );

      if (response.data.success) {
        setMessage({
          type: "success",
          text: response.data.message || "Team member added successfully",
        });
        resetForm();

        setTimeout(() => {
          onSuccess();
          onClose();
          setMessage({ type: "", text: "" });
        }, 1200);
      }
    } catch (error) {
      if (error.response?.data?.field) {
        setFieldError(error.response.data.field, error.response.data.message);
      }

      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Unable to add team member. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => reason !== "backdropClick" && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* ---------------- Header ---------------- */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Add Team Member
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Grant workspace access to a new user
            </Typography>
          </Box>

          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* ---------------- Content ---------------- */}
      <DialogContent sx={{ pt: 3 }}>
        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
        >
          {({ errors, touched, isSubmitting, isValid }) => (
            <Form>
              <Box display="flex" flexDirection="column" gap={2.5}>
                {/* Name */}
                <Field
                  as={TextField}
                  fullWidth
                  autoFocus
                  name="name"
                  label="Full Name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                {/* Email */}
                <Field
                  as={TextField}
                  fullWidth
                  name="email"
                  label="Email Address"
                  type="email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />

                {/* Password */}
                <Field
                  as={TextField}
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  error={touched.password && Boolean(errors.password)}
                  helperText={
                    touched.password
                      ? errors.password
                      : "Use at least 8 characters"
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((p) => !p)}
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Role */}
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="role"
                  label="User Role"
                  error={touched.role && Boolean(errors.role)}
                  helperText={touched.role && errors.role}
                >
                  <MenuItem value="member">
                    <Person fontSize="small" sx={{ mr: 1 }} />
                    Member
                  </MenuItem>
                  <MenuItem value="admin">
                    <AdminPanelSettings fontSize="small" sx={{ mr: 1 }} />
                    Admin
                  </MenuItem>
                  <MenuItem value="viewer">
                    <VisibilityOutlined fontSize="small" sx={{ mr: 1 }} />
                    Viewer
                  </MenuItem>
                </Field>

                {/* Footer Info */}
                <Box>
                  <Chip
                    size="small"
                    label="Admins can manage users & settings"
                    sx={{ bgcolor: "#f0f4ff", color: "#3559e0" }}
                  />
                </Box>

                {/* Actions */}
                <DialogActions sx={{ px: 0, pt: 2 }}>
                  <Button onClick={onClose} color="inherit">
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    variant="contained"
                    sx={{
                      minWidth: 160,
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #4a90e2, #357abd)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #357abd, #2c6cb3)",
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                        Adding…
                      </Box>
                    ) : (
                      "Add Member"
                    )}
                  </Button>
                </DialogActions>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddTeamMemberModal;
